/**
 * One hook for one server read, with the loading and failure states named.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The query client (ADR-022 section 9) is not installed; this is the honest middle
 * step, the same shape the M0 shell used, now shared by every screen: an effect per
 * read, an AbortController per effect, and errors that surface as state instead of
 * crashing the register. Caching and retry arrive with the query client and move into
 * it wholesale.
 */

import { useEffect, useState } from "react";

export type QueryState<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  | { kind: "absence" }
  | { kind: "failed"; error: unknown };

/**
 * Run one read for as long as the screen shows it. `read` receives the abort signal and
 * the token is the caller's business; the hook only manages the lifecycle.
 *
 * The state is not reset synchronously when the dependencies change: the previous
 * answer stays on screen until the new one arrives or the failure is named, which
 * keeps a register steady across a filter change instead of flashing a skeleton.
 */
export function useQuery<T>(
  read: (signal: AbortSignal) => Promise<T>,
  dependencies: readonly unknown[],
): QueryState<T> {
  const [state, setState] = useState<QueryState<T>>({ kind: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    read(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setState({ kind: "ready", data });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setState({ kind: "failed", error });
      });
    return () => {
      controller.abort();
    };
    // The caller owns the dependency list; the read closes over the same values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return state;
}
